import { useCallback, useEffect, useState } from "react";
import { Button, Input, Label, Spinner } from 'reactstrap';
import Select from "react-select";
import makeAnimated from 'react-select/animated';
import { Action, createSelector, ThunkDispatch } from "@reduxjs/toolkit";
import { useDispatch, useSelector } from "react-redux";
import CreatableSelect from 'react-select/creatable';
import { optionsListGetRequest } from "store/auth/action";
import { ApplicationState } from "store";
import Flatpickr from "react-flatpickr";
import { getAvailableStatuses, getStatusOptions } from "helpers/workflowStatusHelper";
import { isJson } from "utils";
import hasPermission from "helpers/hasPermission";
import { currencyGetRequest } from "store/currency/action";
import SelectTree from "Components/Common/SelectTree/SelectTree";
import FormBuilder from "Components/Common/FormBuilder/FormBuilder";

const animatedComponents = makeAnimated();

// Interface for Property
interface PropertyProps {
    key: string;
    label: string | Date;
    type?: string;
    value: string;
    valueJson?: any;
    quick: any;
}

// Interface for Condition
interface ConditionProps {
    label: string;
    value: string;
}

// Interface for TransformedNode
interface TransformedNode {
    label: any;
    value: any;
    createdBy: any;
    createdAt: any;
    updatedAt: any;
    deletedAt: any;
    parentId: any;
    parent: any;
    children?: TransformedNode[]; // Mark children as optional
}

const AddFilter = ({ dataFields, OnBulkUpdate, onCloseClick, t }: any) => {
    const dispatch: ThunkDispatch<any, null, Action<string>> = useDispatch();
    const optionsObj = useSelector((state: ApplicationState) => state.auth.optionData);
    const optionsLoading = useSelector((state: ApplicationState) => state.auth.optionsLoading);
    const userProfile: any = useSelector((state: ApplicationState) => state.auth.userProfile);
    const loading = useSelector((state: ApplicationState) => state.bulk.loading);
    
    const selectDashboardData = createSelector(
        (state: any) => state.Layout,
        (state) => ({
            layoutModeType: state.layoutModeType
        })
    );
    // Inside your component
    const { layoutModeType } = useSelector(selectDashboardData);

    // State Variables
    const [properties, setProperties] = useState<PropertyProps[]>([]);
    const [selectedProperty, setSelectedProperty] = useState<PropertyProps | null>(null);
    const [options, setOptions] = useState<any>([]);
    const [values, setValues] = useState<PropertyProps[]>([]);
    const [value, setValue] = useState<any>(null);
    const [typingTimeout, setTypingTimeout] = useState<NodeJS.Timeout | null>(null);
    
    useEffect(() => {
        dispatch(currencyGetRequest())
        }, [])
        

    // Map data fields to properties on component load
    useEffect(() => {
        const mappedProperties: PropertyProps[] = []
        dataFields?.map((item: any) => {
                const valueJson = item.valuesJson && isJson(item.valuesJson) ? JSON.parse(item.valuesJson) : item.valuesJson;              
                    if (item.permissionsJson && !hasPermission(item.permissionsJson, userProfile?.role?.id)) {
                        return null;
                    }
                    if(valueJson?.type === 'file') return null;
                    mappedProperties.push({
                        label: valueJson?.label,
                        value: valueJson?.key,
                        type: valueJson?.type || "datetime",
                        key: valueJson?.key,
                        quick: valueJson?.filter,
                        valueJson: valueJson || {},
                    });
            })
            .filter(Boolean) as PropertyProps[];
        setProperties(mappedProperties);
    }, [dataFields]);
   
    
    // Update options based on selected property and options data from Redux
    useEffect(() => {
        if (selectedProperty && optionsObj && optionsObj?.[selectedProperty?.valueJson?.model]) {
            if (selectedProperty.type === 'treeselect') {
                const createdNodes: TransformedNode[] = optionsObj?.[selectedProperty?.valueJson?.model].map(transformTree);

                setOptions(createdNodes);
            } else {
                const newOptions = optionsObj?.[selectedProperty?.valueJson?.model].map((option: any) => {
                    const optionsJson = option.valuesJson && isJson(option.valuesJson) ? JSON.parse(option.valuesJson) : option.valuesJson;
                    return { label: optionsJson?.[selectedProperty?.valueJson?.optionLabel], value: option.id, valuesJson: optionsJson, in_forms: true };
                });
                setOptions(newOptions);
            }
        }
    }, [optionsObj?.[selectedProperty?.valueJson?.model], selectedProperty]);

    // Function to find multiple nodes by a list of ids
    const transformTree = (node: any): TransformedNode => {
        // Parse the valuesJson field to extract the name
        const values = node?.valuesJson && isJson(node?.valuesJson) ? JSON.parse(node?.valuesJson) : node?.valuesJson;
        // Create the base transformed node
        const transformedNode: TransformedNode = {
            label: values?.name,
            value: node?.id,
            createdBy: node?.createdBy,
            createdAt: node?.createdAt,
            updatedAt: node?.updatedAt,
            deletedAt: node?.deletedAt,
            parentId: node?.parentId,
            parent: node?.parent,
        };

        // Recursively transform children if they exist and are not empty
        if (node?.children && node?.children.length > 0) {
            transformedNode.children = node?.children.map(transformTree);
        }

        return transformedNode;
    }

    // Handle property selection
    const onSelectProperty = (property: PropertyProps) => {
        if (property) {
            setSelectedProperty(property);
            resetValueStates();
        } 
    };

    // Reset value-related states
    const resetValueStates = () => {
        setValues([]);
        setValue(null);
    };

    // Dispatch option fetch action if a model is associated with the selected property
    const onFocus = () => {
        setOptions([])
        if (selectedProperty?.valueJson?.model) {
            let filter ={
                "filters": [
                  {
                    "quick": [],
                    "advance": [],
                    "search": null
                  }
                ],
                "sorts": [
                  {
                    "field": "name",
                    "order": "asc"
                  }
                ]
            }
            let filterObject=null
            if(selectedProperty?.valueJson?.model == 'sources' || selectedProperty?.valueJson?.responseType) filterObject= {responseType:'treeselect'}
            dispatch(optionsListGetRequest(selectedProperty?.valueJson?.model, filter, 0, 20, filterObject, 'field')); 	
        }
    };    

    const handletypetext = (value: any) => {        
        if (typingTimeout) {
            clearTimeout(typingTimeout);
        }
        const newTimeout = setTimeout(() => {            
            let filter ={
                "filters": [
                {
                    "search": {
                        "term": value,
                        "field": "all"
                    }
                }
                ],
                "sorts": [
                {
                    "field": "name",
                    "order": "asc"
                }
                ]
            }
            let filterObject=null
            if(selectedProperty?.valueJson?.model == 'sources' || selectedProperty?.valueJson?.responseType == 'treeselect') filterObject= {responseType:'treeselect'}
            dispatch(optionsListGetRequest(selectedProperty?.valueJson?.model, filter, 0, 20, filterObject, 'field')); 	
     
        }, 2000);
        setTypingTimeout(newTimeout);
    };

    // Render input field based on property type
    const renderValueInput = () => {
        switch (selectedProperty?.type) {
            case 'datasetselect':
                return (
                    <Select
                        onFocus={onFocus}
                        closeMenuOnSelect={false}
                        components={animatedComponents}
                        isMulti
                        isClearable
                        isSearchable
                        styles={customStyles}
                        onInputChange={(inputValue: any) => handletypetext(inputValue)}
                        name="values"
                        onChange={setValues}
                        value={values}
                        isLoading={optionsLoading}                     
                        options={optionsLoading ? [{ label: 'Loading...', value: null }] : options || []}
                    />
                );
            case 'select':
            case 'checkbox':
            case 'selectboxes':
                let valueOptions = selectedProperty?.valueJson?.values || []
                let isMulti = selectedProperty?.valueJson?.isMulti || false
                if (selectedProperty?.valueJson?.process_flows?.length) {
                    const { options: groupOptions } = getStatusOptions(selectedProperty?.valueJson?.process_flows)
                    valueOptions = groupOptions;
                }
                return (
                    <Select
                        closeMenuOnSelect={false}
                        components={animatedComponents}
                        isMulti={isMulti}
                        isClearable
                        isSearchable
                        styles={customStyles}
                        name="values"
                        options={(valueOptions?.length && valueOptions?.filter((item: any) => item?.in_forms)) || []}
                        onChange={setValues}
                        value={values}
                    />
                );
            case 'textfield':
            case 'textarea':
            case 'email':
            case 'phone':
                return (
                    <CreatableSelect
                        closeMenuOnSelect={false}
                        components={animatedComponents}
                        isClearable
                        isSearchable
                        styles={customStyles}
                        name="values"
                        options={[]}
                        onChange={(newValue: any) => setValues(newValue)}
                        value={values}
                    />
                );
            case 'datetime':
                return (
                    <Flatpickr
                        className="form-control w-100 h-100 text-start"
                        value={value ? value : undefined}
                        onChange={([date]) => setValue(date)}
                    />
                );
            case 'number':
                return (
                    <Input
                        type="number"
                        name="label"
                        styles={customStyles}
                        value={value ? value.toString() : undefined}
                        onChange={(e: any) => {setValue(e.target.value);}}
                        placeholder='Enter value'
                    />
                );
            case 'treeselect':
                return (
                    <SelectTree
                        setValues={(values: any) => setValues(values)}
                        onFocus={onFocus}
                        values={values}
                        showValues={true}
                        optionsLoading={optionsLoading}
                        options={optionsLoading ? [{ label: 'Loading...', value: null }] : options || []}
                    />
                );
            default:
                return null;
        }
    };

    const customStyles = {
        // Customize the style of the dropdown menu
        input: (provided: any) => ({
            ...provided,
            color: layoutModeType === 'light'? 'black' : 'white',
          }),
        control: (provided: any, state: any) => ({
                    ...provided,
                    backgroundColor: 'transparent'
        }),
        menu: (provided: any) => ({
          ...provided,
          backgroundColor: layoutModeType === 'light'? 'white' : '#495057',
          color: layoutModeType === 'light'? 'black' : 'white',
        }),
        // Customize the style of the option in the dropdown menu
        option: (provided: any, state: any) => ({
          ...provided,
          backgroundColor: layoutModeType === 'light'? 'white' : '#495057',
          color: layoutModeType === 'light'? 'black' : 'white',
          '&:hover': {
            backgroundColor: layoutModeType === 'light' ? 'white' : '#495057',
          },
        }),
    }
    const renderOptions = (selectedProperty: any) =>  {
        let statusoptions = selectedProperty?.valueJson.values && selectedProperty?.valueJson.values.reverse() || [];
        if (selectedProperty?.valueJson?.process_flows && (selectedProperty?.valueJson.key == 'application_status' || selectedProperty?.valueJson.key == 'student_status')) {
            const { statuses } = getAvailableStatuses(selectedProperty?.valueJson?.process_flows, '', '', true);
            statusoptions = statuses
        }
        return statusoptions;
    }

    return (
        <div className="d-flex flex-column gap-2 bg-light p-3">
            <div>
                <Label>Select Field to Update</Label>
                <Select
                    className="basic-single"
                    classNamePrefix="select"
                    isClearable
                    isSearchable
                    name="properties"
                    options={properties}
                    styles={customStyles}
                    onChange={onSelectProperty}
                    value={selectedProperty}
                />
            </div>
            {selectedProperty && (
                <div className="d-flex flex-column gap-2">
                   
                    <FormBuilder
                        placeholder={selectedProperty?.valueJson.placeholder}
                        name={selectedProperty?.valueJson.key}
                        onChange={(e: any) => setValue(e?.value || e)}
                        options={renderOptions(selectedProperty)}
                        label={selectedProperty?.valueJson.label}
                        type={selectedProperty?.valueJson.type}
                        value={value || ''}
                        isEditState={true}
                        actionButtons={false}
                        Editable={true}
                        rest={selectedProperty?.valueJson}
                        invalid={true}
                    />
                    {/* {renderValueInput()} */}
                </div>
            )}
               
            <div className="hstack gap-2 justify-content-center">
                <Button color='primary' className="btn btn-primary" disabled={loading} onClick={() => OnBulkUpdate({[selectedProperty?.valueJson.key] : value })}>
                    {loading ? <Spinner size="sm" type="grow" className="flex-shrink-0" role="status"> Loading... </Spinner>: null }{" "}
                    {t("properties.update")}
                </Button>
                <Button color="danger" onClick={() => onCloseClick()} outline>
                    {t("common.offcanvas.import_cancel")}
                </Button>
            </div>
        </div>
    );
};

export default AddFilter;
